import {
  Subsystem,
  SubsystemElement,
  PlacedBlock,
  SocketConnection,
  PlacedLabel,
  TextAnnotation,
  IsoPoint,
  Transform,
} from '@/app/types/cad';
import { transformPoint } from '@/app/utils/transform';

export interface PlacedSubsystemResult {
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  labels: PlacedLabel[];
  texts: TextAnnotation[];
  subsystemInstanceId: string;
}

/**
 * Places a subsystem at a given position with optional transform
 * Creates new instances of all contained elements with proper ID hierarchy
 * 
 * @param subsystem - The subsystem to place
 * @param position - Position where to place the subsystem (its center point)
 * @param systemId - Optional system ID for the placed elements
 * @param transform - Optional transform (rotation, scale, mirror) to apply to entire subsystem
 * @returns All placed elements with new IDs
 */
export function placeSubsystem(
  subsystem: Subsystem,
  position: IsoPoint,
  systemId?: string,
  transform?: Partial<Transform>
): PlacedSubsystemResult {
  // Generate unique subsystem instance ID
  const subsystemInstanceId = `subsys-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Calculate offset from subsystem center to placement position
  const offset: IsoPoint = {
    u: position.u - subsystem.centerPoint.u,
    v: position.v - subsystem.centerPoint.v,
  };

  // Default transform for the entire subsystem
  const subsystemTransform: Transform = {
    position: { u: 0, v: 0 },
    rotation: transform?.rotation ?? 0,
    scale: transform?.scale ?? 1.0,
    mirrorX: transform?.mirrorX ?? false,
    mirrorY: transform?.mirrorY ?? false,
  };

  const blocks: PlacedBlock[] = [];
  const connections: SocketConnection[] = [];
  const labels: PlacedLabel[] = [];
  const texts: TextAnnotation[] = [];

  // Track old ID to new ID mapping for connections
  const blockIdMap = new Map<string, string>();
  const connectionIdMap = new Map<string, string>();
  const labelIdMap = new Map<string, string>();
  const textIdMap = new Map<string, string>();

  // Process all elements
  subsystem.elements.forEach(element => {
    switch (element.type) {
      case 'block':
        processBlockElement(
          element,
          offset,
          subsystemTransform,
          systemId,
          subsystemInstanceId,
          blocks,
          blockIdMap
        );
        break;

      case 'connection':
        processConnectionElement(
          element,
          offset,
          subsystemTransform,
          systemId,
          subsystemInstanceId,
          connections,
          connectionIdMap,
          blockIdMap
        );
        break;

      case 'label':
        processLabelElement(
          element,
          offset,
          subsystemTransform,
          labels,
          labelIdMap,
          blockIdMap,
          connectionIdMap,
          textIdMap
        );
        break;

      case 'text-annotation':
        processTextElement(
          element,
          offset,
          subsystemTransform,
          texts,
          textIdMap
        );
        break;
    }
  });

  return {
    blocks,
    connections,
    labels,
    texts,
    subsystemInstanceId,
  };
}

function processBlockElement(
  element: SubsystemElement,
  offset: IsoPoint,
  subsystemTransform: Transform,
  systemId: string | undefined,
  subsystemInstanceId: string,
  blocks: PlacedBlock[],
  blockIdMap: Map<string, string>
) {
  const originalBlock = element.data as PlacedBlock;
  const newBlockId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Store mapping
  blockIdMap.set(originalBlock.id, newBlockId);

  // Calculate new position (relative position + offset + subsystem transform)
  let newPosition = {
    u: element.relativePosition.u + offset.u,
    v: element.relativePosition.v + offset.v,
  };

  // Apply subsystem-level transform to position if rotation/mirror is applied
  if (subsystemTransform.rotation !== 0 || subsystemTransform.mirrorX || subsystemTransform.mirrorY) {
    // Transform relative to subsystem center (offset position)
    const relativeToCenter = {
      u: newPosition.u - offset.u,
      v: newPosition.v - offset.v,
    };
    
    const transformed = transformPoint(relativeToCenter, subsystemTransform);
    newPosition = {
      u: transformed.u + offset.u,
      v: transformed.v + offset.v,
    };
  }

  // Combine block's own transform with subsystem transform
  const combinedTransform: Transform = {
    position: newPosition,
    rotation: (originalBlock.transform.rotation + subsystemTransform.rotation) % 360,
    scale: originalBlock.transform.scale * subsystemTransform.scale,
    mirrorX: subsystemTransform.mirrorX ? !originalBlock.transform.mirrorX : originalBlock.transform.mirrorX,
    mirrorY: subsystemTransform.mirrorY ? !originalBlock.transform.mirrorY : originalBlock.transform.mirrorY,
  };

  const newBlock: PlacedBlock = {
    ...originalBlock,
    id: newBlockId,
    transform: combinedTransform,
    systemId,
    subsystemId: subsystemInstanceId,
  };

  blocks.push(newBlock);
}

function processConnectionElement(
  element: SubsystemElement,
  offset: IsoPoint,
  subsystemTransform: Transform,
  systemId: string | undefined,
  subsystemInstanceId: string,
  connections: SocketConnection[],
  connectionIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
) {
  const originalConn = element.data as SocketConnection;
  const newConnId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  connectionIdMap.set(originalConn.id, newConnId);

  // Calculate new position with subsystem transform
  let newPosition = {
    u: element.relativePosition.u + offset.u,
    v: element.relativePosition.v + offset.v,
  };

  if (subsystemTransform.rotation !== 0 || subsystemTransform.mirrorX || subsystemTransform.mirrorY) {
    const relativeToCenter = {
      u: newPosition.u - offset.u,
      v: newPosition.v - offset.v,
    };
    const transformed = transformPoint(relativeToCenter, subsystemTransform);
    newPosition = {
      u: transformed.u + offset.u,
      v: transformed.v + offset.v,
    };
  }

  // Update connected sockets to reference new block IDs
  const newConnectedSockets = originalConn.connectedSockets.map(socket => ({
    ...socket,
    blockId: blockIdMap.get(socket.blockId) || socket.blockId, // Use new ID if available
  }));

  const newConnection: SocketConnection = {
    ...originalConn,
    id: newConnId,
    position: newPosition,
    connectedSockets: newConnectedSockets,
    systemId,
  };

  connections.push(newConnection);
}

function processLabelElement(
  element: SubsystemElement,
  offset: IsoPoint,
  subsystemTransform: Transform,
  labels: PlacedLabel[],
  labelIdMap: Map<string, string>,
  blockIdMap: Map<string, string>,
  connectionIdMap: Map<string, string>,
  textIdMap: Map<string, string>
) {
  const originalLabel = element.data as PlacedLabel;
  const newLabelId = `label-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  labelIdMap.set(originalLabel.id, newLabelId);

  // Calculate new position with subsystem transform
  let newPosition = {
    u: element.relativePosition.u + offset.u,
    v: element.relativePosition.v + offset.v,
  };

  if (subsystemTransform.rotation !== 0 || subsystemTransform.mirrorX || subsystemTransform.mirrorY) {
    const relativeToCenter = {
      u: newPosition.u - offset.u,
      v: newPosition.v - offset.v,
    };
    const transformed = transformPoint(relativeToCenter, subsystemTransform);
    newPosition = {
      u: transformed.u + offset.u,
      v: transformed.v + offset.v,
    };
  }

  // Update target element ID to reference new IDs
  let newTargetElementId = originalLabel.targetElementId;
  
  switch (originalLabel.targetElementType) {
    case 'block':
      newTargetElementId = blockIdMap.get(originalLabel.targetElementId) || originalLabel.targetElementId;
      break;
    case 'connection':
      newTargetElementId = connectionIdMap.get(originalLabel.targetElementId) || originalLabel.targetElementId;
      break;
    case 'text-annotation':
      newTargetElementId = textIdMap.get(originalLabel.targetElementId) || originalLabel.targetElementId;
      break;
  }

  const newLabel: PlacedLabel = {
    ...originalLabel,
    id: newLabelId,
    position: newPosition,
    targetElementId: newTargetElementId,
  };

  labels.push(newLabel);
}

function processTextElement(
  element: SubsystemElement,
  offset: IsoPoint,
  subsystemTransform: Transform,
  texts: TextAnnotation[],
  textIdMap: Map<string, string>
) {
  const originalText = element.data as TextAnnotation;
  const newTextId = `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  textIdMap.set(originalText.id, newTextId);

  // Calculate new position with subsystem transform
  let newPosition = {
    u: element.relativePosition.u + offset.u,
    v: element.relativePosition.v + offset.v,
  };

  if (subsystemTransform.rotation !== 0 || subsystemTransform.mirrorX || subsystemTransform.mirrorY) {
    const relativeToCenter = {
      u: newPosition.u - offset.u,
      v: newPosition.v - offset.v,
    };
    const transformed = transformPoint(relativeToCenter, subsystemTransform);
    newPosition = {
      u: transformed.u + offset.u,
      v: transformed.v + offset.v,
    };
  }

  // Adjust rotation if subsystem is rotated
  let newRotation = originalText.rotation;
  if (subsystemTransform.rotation !== 0) {
    newRotation += (subsystemTransform.rotation * Math.PI) / 180;
  }

  const newText: TextAnnotation = {
    ...originalText,
    id: newTextId,
    position: newPosition,
    rotation: newRotation,
  };

  texts.push(newText);
}

/**
 * Get all external connection points from a subsystem
 * These are connection points that can be used to connect to other elements
 */
export function getSubsystemExternalConnections(
  subsystem: Subsystem,
  position: IsoPoint
): IsoPoint[] {
  const externalPoints: IsoPoint[] = [];
  
  // Calculate offset
  const offset: IsoPoint = {
    u: position.u - subsystem.centerPoint.u,
    v: position.v - subsystem.centerPoint.v,
  };

  // Get all blocks and their connection points from elements
  subsystem.elements.forEach(element => {
    if (element.type === 'block' && element.data) {
      const block = element.data as PlacedBlock;
      const blockData = block.blockData;

      if (!blockData || !blockData.elements) {
        return;
      }

      // Find connection elements within the block
      blockData.elements.forEach(el => {
        if (el.type === 'connection' && el.position) {
          // Transform connection point
          const worldPos = transformPoint(el.position, block.transform);
          
          // Add offset for subsystem placement
          externalPoints.push({
            u: worldPos.u + offset.u,
            v: worldPos.v + offset.v,
          });
        }
      });
    }
  });

  return externalPoints;
}